import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Select } from "antd";
import axios from "axios";
import { BASE_URL } from "../../Constant";
import styles from "./index.module.scss";
import EngagementCentreStone from "./EngagementCentreStone";
import { isNonRingProduct, offersFingerSize } from "../../utils/ringSize";

/**
 * Customer configurator for the dedicated halo engagement build
 * (productType "engagement-ring" with a product.engagement recipe).
 *
 * Renders the SAME layout as every other ring — Select Metal Type · Select
 * Colour · Select Size · Centre Diamond (the EngagementCentreStone modal) ·
 * Select Diamond Quality for Accent/Side Stones — but driven by the engagement
 * recipe instead of variations, and priced live by the backend engine
 * (GET /product/engagement-price/:id), the single source of truth shared with
 * checkout. Metal karat + finger size + accent quality + centre carat/colour/
 * clarity all re-price; gold colour just swaps the hero image (same price).
 */

const GOLD = "#C5A028";

const COLOUR_MAP = {
  "White Gold": { bg: "linear-gradient(135deg, #E8E8E8, #F5F5F5, #D8D8D8)", activeBg: "linear-gradient(135deg, #C0C0C0, #D8D8D8, #B0B0B0)", border: "#999", text: "#777", activeText: "#fff" },
  "Yellow Gold": { bg: "linear-gradient(135deg, #F0D860, #F5E080, #E8C840)", activeBg: "linear-gradient(135deg, #B8922A, #C5A028, #A88520)", border: "#8B6914", text: "#9B7A20", activeText: "#fff" },
  "Rose Gold": { bg: "linear-gradient(135deg, #F0C8B8, #F5D0C0, #E8B8A8)", activeBg: "linear-gradient(135deg, #C08878, #B87868, #A87060)", border: "#8B5E52", text: "#9A7068", activeText: "#fff" },
};

function metalLabel(m) {
  if (!m) return "";
  if (String(m.metal).toLowerCase() === "gold") return `${String(m.purity || "").toUpperCase()} Gold`;
  return m.metal; // Platinum / Silver
}

export default function EngagementBuilder({ product, onChange, applyRetailerPricing, formatPrice, qualityLabels, onShowSizeChart, onColourImage, chainSlot = null, enamelSlot = null, addOnPrice = 0 }) {
  const eng = product?.engagement || {};
  const adjust = typeof applyRetailerPricing === "function" ? applyRetailerPricing : (x) => x;
  const fmt = typeof formatPrice === "function" ? formatPrice : (x) => Number(x || 0).toFixed(2);
  const accentQualities = useMemo(() => eng.accentQualities || [], [eng.accentQualities]);
  const qLabel = (k) => (qualityLabels && qualityLabels[k]) || String(k || "").replace(/_/g, " ").toUpperCase();

  const metals = useMemo(() => eng.metals || [], [eng.metals]);
  // All 3 gold colours show by default (like every other product); a colour is
  // hidden only if the recipe explicitly deactivates it. colourImages maps a
  // photo per colour (optional) — colour is aesthetic, same price.
  const colourVariants = useMemo(() => {
    const ci = eng.colourImages || [];
    return ["White Gold", "Yellow Gold", "Rose Gold"]
      .filter((colour) => { const e = ci.find((c) => c.colour === colour); return !e || e.active !== false; })
      .map((colour) => { const e = ci.find((c) => c.colour === colour); return { colour, imageIndex: e ? e.imageIndex : -1 }; });
  }, [eng.colourImages]);
  const sizes = useMemo(() => eng.sizes || [], [eng.sizes]);
  const PLACEHOLDER_SIZE_RX = /^(pair|default|one size|n\/?a|-|—)?$/i;
  // Deliberately phrased as "not obviously a non-ring" rather than "is a ring":
  // every OTHER product on this builder is a ring, and some are named without
  // the word (Seraphina, Aurelia…). Requiring a positive match would silently
  // drop the chart from those. Only a clear pendant/necklace/earring/bracelet
  // signal hides it. See utils/ringSize.js.
  const isRingProduct = !isNonRingProduct(product);
  // Two ways there is nothing to ask:
  //   · the only entry is a placeholder — a pendant here carries ONE nominal
  //     size purely so its bail weight (the shank) prices, and a lone
  //     "Default"/"One Size" pill is noise;
  //   · the ladder is finger sizes and the piece has no finger. SKU 45335 is a
  //     PAIR of drop earrings and was offering all 29 UK rungs, because the
  //     admin form seeds that ladder when `sizes` is left empty
  //     (EngagementBuildForm.jsx ~L361) so empty is not an option.
  // The size is still chosen underneath — index 0 — so pricing and the saved
  // build recipe are unaffected; the label is dropped from the order line
  // server-side (backend/utils/ringSize.js).
  const hasChoosableSize =
    (sizes.length > 1 || (sizes.length === 1 && !PLACEHOLDER_SIZE_RX.test(String(sizes[0]).trim()))) &&
    offersFingerSize(product, sizes);
  const widths = useMemo(() => eng.widths || [], [eng.widths]);
  const coverages = useMemo(() => eng.coverages || [], [eng.coverages]);
  const carats = useMemo(() => [...(eng.carats || [])].map(Number).filter((n) => n > 0).sort((a, b) => a - b), [eng.carats]);

  const [metalKey, setMetalKey] = useState(eng.baseMetalKey || metals[0]?.key || "");
  // Default to Yellow Gold when offered (else the first available colour).
  const [metalColour, setMetalColour] = useState(() => {
    const names = colourVariants.map((c) => c.colour);
    return names.includes("Yellow Gold") ? "Yellow Gold" : (names[0] || null);
  });
  const [sizeIndex, setSizeIndex] = useState(0);
  const [widthIndex, setWidthIndex] = useState(0);
  const [coverageIndex, setCoverageIndex] = useState(0);
  const [centre, setCentre] = useState(null); // { carat, colour, clarity } from the modal
  const [quality, setQuality] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cheapestCentre, setCheapestCentre] = useState(null); // pre-selected default diamond
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onColourImageRef = useRef(onColourImage);
  onColourImageRef.current = onColourImage;

  const noCentre = !!eng.noCentre; // band/eternity — no centre stone
  // Effective centre for pricing: the chosen stone, else smallest carat + first
  // offered colour/clarity (a representative "from" price until one is picked).
  // Band/eternity builds have no centre — all undefined (ring prices off size).
  // The carat the page OPENS on: the build's chosen default when it names one,
  // otherwise the smallest offered (which is what the cheapest stone resolves to).
  // Stated as a minimum, so a default that is not itself on the ladder still lands
  // on a real, offered carat rather than nothing.
  const openingCarat = (() => {
    const want = Number(eng.defaultCarat) || 0;
    if (!want) return carats[0] || 0;
    return carats.find((c) => Number(c) >= want) ?? carats[carats.length - 1] ?? 0;
  })();
  const effCarat = noCentre ? undefined : (centre?.carat || openingCarat || 0);
  const effColour = noCentre ? undefined : (centre?.colour || (eng.colours || [])[0] || undefined);
  const effClarity = noCentre ? undefined : (centre?.clarity || (eng.clarities || [])[0] || undefined);

  // White/Yellow/Rose colour only applies to GOLD. Platinum/Silver have no gold
  // colour, so the colour picker (and its hero-image swap) is hidden for them.
  const selectedMetal = metals.find((m) => m.key === metalKey);
  const isGoldMetal = String(selectedMetal?.metal || "").toLowerCase() === "gold";

  // Re-price on any price-affecting change (metal karat, size, centre, quality).
  useEffect(() => {
    if (!product?._id || (!noCentre && !effCarat)) return;
    let cancelled = false;
    setLoading(true);
    // The price is trade data, so the route wants the signed-in retailer's token.
    // This page sits behind AuthGate, so it is always there.
    const token = localStorage.getItem("token");
    axios.get(`${BASE_URL}/product/engagement-price/${product._id}`, {
      params: { metal: metalKey || undefined, sizeIndex, widthIndex, coverageIndex, carat: effCarat, colour: effColour, clarity: effClarity, quality: quality || undefined },
      headers: token ? { Authorization: token } : {},
    })
      .then((res) => {
        if (cancelled) return;
        const b = res?.data || null;
        setBreakdown(b);
      })
      .catch(() => { if (!cancelled) setBreakdown(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?._id, metalKey, sizeIndex, widthIndex, coverageIndex, effCarat, effColour, effClarity, quality]);

  // Keep the selected accent quality consistent with the centre-type-filtered
  // offered list (Lab centre → lab_* accents; Natural → natural_*). Sets a
  // sensible default and switches if the centre type ever changes.
  useEffect(() => {
    const opts = breakdown?.qualityOptions || [];
    if (!opts.length) return;
    const prefix = centre?.type === "natural" ? "natural_" : "lab_";
    const allowed = opts.filter((o) => accentQualities.includes(o.quality));
    const byType = allowed.filter((o) => String(o.quality).startsWith(prefix));
    // Band/eternity rings have no centre stone to dictate the accent type, so
    // offer every ticked quality (lab AND natural). Centre rings follow the centre.
    // The type fallback rescues a MISMATCH — ticked natural qualities on a lab
    // centre — but must not fire on an EMPTY list, which now means the merchant
    // offers no side-stone choice at all.
    const pool = noCentre
      ? allowed
      : (byType.length
          ? byType
          : (accentQualities.length ? opts.filter((o) => String(o.quality).startsWith(prefix)) : []));
    if (!pool.length) return;
    if (!quality || !pool.some((o) => o.quality === quality)) setQuality(pool[0].quality);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdown, centre?.type, accentQualities, quality]);

  // Lift the live total + full selection up to AddCart (price block + cart).
  useEffect(() => {
    onChangeRef.current?.({
      totalGBP: Number(breakdown?.totalGBP || 0),
      hasCentre: !!centre,
      selection: {
        metalKey, sizeIndex, widthIndex, coverageIndex, metalColour: isGoldMetal ? metalColour : null,
        // Product Details → Specifications reads this. Null when the size was
        // never asked for, so the card falls back to "—" rather than putting a
        // finger size on a pair of earrings — the same thing the selector above
        // stops doing. Before this the card showed the vestigial variation's
        // "Default" on every build, ring or not.
        sizeLabel: (hasChoosableSize && sizes[sizeIndex]) || null,
        carat: effCarat, colour: effColour, clarity: effClarity,
        quality: quality || breakdown?.melee?.quality || null,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdown, metalKey, sizeIndex, widthIndex, coverageIndex, metalColour, centre, quality, isGoldMetal]);

  // Between golds only a colour pick swaps the hero image (same price). Platinum/
  // Silver have no colour or photos of their own, so selecting one shows the White
  // Gold shot (the closest look — the 360 maps them to white too), and returning to
  // a gold puts the chosen colour's photo back. Driven from the clicks rather than
  // an effect, so re-picking the ticked colour still brings its photo back after
  // the customer has browsed the thumbnails.
  const showColourImage = (colour) => {
    const cv = colourVariants.find((c) => c.colour === colour);
    const path = cv && cv.imageIndex >= 0 ? product?.Product_Images?.[cv.imageIndex]?.path : null;
    if (path) onColourImageRef.current?.(path);
  };
  const selectMetal = (key) => {
    const toGold = String(metals.find((m) => m.key === key)?.metal || "").toLowerCase() === "gold";
    setMetalKey(key);
    if (!toGold) showColourImage("White Gold");
    else if (!isGoldMetal && metalColour) showColourImage(metalColour);
  };
  // Open on the photo for what the build starts on: the pre-selected colour on a
  // gold, the White Gold shot on Platinum/Silver.
  useEffect(() => {
    showColourImage(isGoldMetal ? metalColour : "White Gold");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?._id]);

  // Cheapest configured centre diamond — pre-selected by default so the price
  // visibly includes a stone (the customer can Change it or clear it).
  useEffect(() => {
    if (noCentre) { setCheapestCentre(null); return; }
    const cs = product?.centreStone || {};
    const all = (eng.carats && eng.carats.length ? eng.carats : cs.carats) || [];
    // Pre-select from the opening carat UP. Handing the whole ladder to
    // /centre-stone/lab-cheapest always came back with the smallest carat, so a
    // build that names a default never got it. Narrowing the search is what makes
    // the price on load describe the stone in the photograph.
    const cts = all.filter((c) => Number(c) >= openingCarat);
    if (!product?._id || !cts.length) { setCheapestCentre(null); return; }
    axios.get(`${BASE_URL}/centre-stone/lab-cheapest`, {
      params: {
        shape: eng.shape || cs.shape || "Round",
        feed: eng.feed || "labgrownWhite",
        carats: cts.join(","),
        colours: ((eng.colours && eng.colours.length ? eng.colours : cs.colours) || []).join(","),
        clarities: ((eng.clarities && eng.clarities.length ? eng.clarities : cs.clarities) || []).join(","),
      },
    })
      .then((res) => setCheapestCentre(res?.data?.found ? res.data : null))
      .catch(() => setCheapestCentre(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?._id, openingCarat]);

  if (!eng.enabled) return null;
  const qualityOptions = breakdown?.qualityOptions || [];
  // Accent stones follow the CENTRE stone's type: Lab centre → lab_* accents,
  // Natural centre → natural_* accents (FG VS / GH SI / HI SI). Natural centre
  // is disabled for now, so this is effectively lab-only today.
  //
  // An EMPTY accentQualities means the merchant offers NO side-stone choice, and
  // the block below hides. It used to mean "offer them all", which made
  // de-selecting every quality in the admin a no-op — the customer kept seeing
  // the full set (reported on SKU 45080, a plain band with no side stones at
  // all). Nothing in the catalogue relied on the old reading: every product that
  // has accent stones names its qualities explicitly.
  const typePrefix = centre?.type === "natural" ? "natural_" : "lab_";
  const matchesType = (q) => String(q.quality).startsWith(typePrefix);
  const allowedQualities = qualityOptions.filter((q) => accentQualities.includes(q.quality));
  const byTypeAllowed = allowedQualities.filter(matchesType);
  // Band/eternity rings (no centre) offer every ticked quality regardless of
  // lab/natural; centre rings filter accents to the centre stone's type. The
  // fallback rescues a TYPE MISMATCH only — never an empty list.
  const offeredQualities = noCentre
    ? allowedQualities
    : (byTypeAllowed.length
        ? byTypeAllowed
        : (accentQualities.length ? qualityOptions.filter(matchesType) : []));

  return (
    <>
      {/* Select Metal Type */}
      {metals.length > 0 && (
        <div className={styles.configStep}>
          <span className={styles.stepLabel}>Select Metal Type</span>
          <div className={styles.variationGrid}>
            {metals.map((m) => (
              <div key={m.key} className={`${styles.variationCard} ${metalKey === m.key ? styles.activeVariation : ""}`} onClick={() => selectMetal(m.key)}>
                {metalLabel(m)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Select Colour — gold only (Platinum/Silver have no colour variant) */}
      {isGoldMetal && colourVariants.length > 0 && (
        <div className={styles.configStep}>
          <span className={styles.stepLabel}>Select Colour</span>
          <div className={styles.colorGrid}>
            {colourVariants.map((cv) => {
              const cm = COLOUR_MAP[cv.colour] || { bg: "#f5f5f5", activeBg: "#333", border: "#333", text: "#555", activeText: "#fff" };
              const isActive = metalColour === cv.colour;
              return (
                <div key={cv.colour} className={styles.colorCard} onClick={() => { setMetalColour(cv.colour); showColourImage(cv.colour); }}
                  style={{ background: isActive ? cm.activeBg : cm.bg, border: isActive ? `2px solid ${cm.border}` : "1px solid rgba(0,0,0,0.08)", color: isActive ? cm.activeText : cm.text, boxShadow: isActive ? "0 3px 10px rgba(0,0,0,0.2)" : "none" }}>
                  {isActive && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ position: "absolute", top: 5, left: 6, width: 11, height: 11 }}>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                  {cv.colour}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Select Width — band variant (e.g. eternity rings offered in several widths).
          A dropdown rather than pills: a signet offers eleven widths, which wrapped to
          three rows of buttons and dominated the page above the size and the stone.
          Same antd Select and the same two classes the size selector uses above ten
          options, so the two controls read as one control repeated.
          Keyed on the INDEX, not the label — width labels are typed by hand in admin,
          so two can collide, and the pricing engine addresses widths by position. */}
      {widths.length > 1 && (
        <div className={styles.configStep}>
          <span className={styles.stepLabel}>Select Width</span>
          <Select value={widthIndex} onChange={(v) => setWidthIndex(Number(v))}
            className={styles.sizeDropdown} popupClassName={styles.sizeDropdownPopup}
            options={widths.map((w, i) => ({ value: i, label: w.label || `Width ${i + 1}` }))} />
        </div>
      )}

      {/* Select Coverage — stone-count variant (e.g. half vs full eternity) */}
      {coverages.length > 1 && (
        <div className={styles.configStep}>
          <span className={styles.stepLabel}>Select Coverage</span>
          <div className={styles.sizeGrid}>
            {coverages.map((c, i) => (
              <button key={i} type="button" onClick={() => setCoverageIndex(i)} className={`${styles.sizePill} ${coverageIndex === i ? styles.activeSize : ""}`}>{c.label || `Coverage ${i + 1}`}</button>
            ))}
          </div>
        </div>
      )}

      {/* Select Size — see hasChoosableSize above for when there is nothing to
          ask. The conversion chart is ring-only on top of that: a pendant with
          a genuine chain length still has a size, but never a UK/US/EU one. */}
      {hasChoosableSize && (
        <div className={styles.configStep}>
          <div className={styles.stepLabelRow}>
            <span className={styles.stepLabel}>Select Size</span>
            {isRingProduct && (
              <button type="button" className={styles.ringSizeLink} onClick={() => onShowSizeChart && onShowSizeChart()}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                Ring Size Conversion Chart
              </button>
            )}
          </div>
          {sizes.length > 10 ? (
            <Select value={sizes[sizeIndex]} onChange={(v) => setSizeIndex(Math.max(0, sizes.indexOf(v)))} placeholder="Choose a size"
              className={styles.sizeDropdown} popupClassName={styles.sizeDropdownPopup} options={sizes.map((s) => ({ value: s, label: s }))} />
          ) : (
            <div className={styles.sizeGrid}>
              {sizes.map((s, i) => (
                <button key={i} type="button" onClick={() => setSizeIndex(i)} className={`${styles.sizePill} ${sizeIndex === i ? styles.activeSize : ""}`}>{s}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Centre Diamond — the existing chooser modal (hidden for band/eternity) */}
      {!noCentre && (
        <div className={styles.configStep}>
          <span className={styles.stepLabel}>Centre Diamond</span>
          <EngagementCentreStone centreStone={product.centreStone} initialSelection={cheapestCentre} onChange={(sel) => setCentre(sel?.carat ? { carat: sel.carat, colour: sel.colour, clarity: sel.clarity, type: sel.type } : null)} />
        </div>
      )}

      {/* The chain step, handed down from AddCart. It belongs here rather than after this
          component: the quality cards below quote a FULL price, so a chain added after them
          would leave those figures disagreeing with the headline — the same reason it sits
          between Size and Quality on the ordinary product path. */}
      {chainSlot}

      {/* …and the picture-enamelling step, for the identical reason. */}
      {enamelSlot}

      {/* Select Diamond Quality for Accent/Side Stones */}
      {offeredQualities.length > 0 && (
        <div className={styles.configStep}>
          <span className={styles.stepLabel}>Select Diamond Quality for Accent/Side Stones</span>
          <div className={styles.qualityGrid}>
            {offeredQualities.map((q) => (
              <button type="button" key={q.quality} onClick={() => setQuality(q.quality)} className={`${styles.qualityCard} ${quality === q.quality ? styles.activeQuality : ""}`}>
                <div className={styles.qualityLabel}>{qLabel(q.quality)}</div>
                {/* + the add-ons, for the same reason the ordinary path's cards carry them:
                    this is a full price, and the headline above includes the chain and the
                    enamelling. Both are OUTSIDE adjust() — pass-through at cost, never
                    discounted or marked up, exactly as withAddOns() does it in AddCart. */}
                <div className={styles.qualityPrice}>{"£"}{fmt(Number(adjust(q.totalGBP) || 0) + Number(addOnPrice || 0))}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
